import { Box, Button } from '@ltvco/refresh-lib/theme';
import { WhereIsMyVINModal } from '@ltvco/refresh-lib/v2';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  Control,
  UseFormTrigger,
  UseFormClearErrors,
} from '@ltvco/refresh-lib/vendors';
import { HelpOutline } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';

import { SearchInput } from '../SearchInput';

import {
  VehicleInputsErrorTextPositionWrapper,
  VehicleSearchFieldContainer,
} from './components';
import { VehicleFormData } from './utils';

interface VINSearchInputProps {
  placeholderText: string;
  register: UseFormRegister<VehicleFormData>;
  errors: FieldErrors<VehicleFormData>;
  clearErrors: UseFormClearErrors<VehicleFormData>;
  setValue: UseFormSetValue<VehicleFormData>;
  control: Control<VehicleFormData>;
  trigger: UseFormTrigger<VehicleFormData>;
  value: string;
  onSubmit?: () => void;
  isMobile: boolean;
}

export const VINSearchInput = React.memo(
  ({
    placeholderText,
    register,
    errors,
    clearErrors,
    setValue,
    control,
    trigger,
    value,
    onSubmit,
    isMobile,
  }: VINSearchInputProps) => {
    const registerReturn = register('vin', { required: 'VIN is required' });
    const [vinValue, setVinValue] = useState(value);
    const [isVINModalOpen, setIsVINModalOpen] = useState(false);

    const handleClear = () => {
      setValue('vin', '');
      clearErrors('vin');
      setVinValue('');
    };

    const handleCustomChange = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      // eslint-disable-next-line no-unused-vars
      field: { onChange: (value: string) => void }
    ) => {
      // Strip out non-alphanumeric characters and convert to uppercase
      const sanitizedValue = e.target.value
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
      field.onChange(sanitizedValue);
      setValue('vin', sanitizedValue);
      setVinValue(sanitizedValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSubmit) {
        e.preventDefault();
        trigger().then((isValid) => {
          if (isValid) {
            onSubmit();
          }
        });
      }
    };

    const SearchInputComponent = () => {
      return (
        <VehicleInputsErrorTextPositionWrapper sx={{ width: '100%' }}>
          <SearchInput
            registerReturn={registerReturn}
            label=""
            placeholder={placeholderText}
            error={errors.vin}
            clearErrors={clearErrors as unknown as () => void}
            onClear={handleClear}
            value={vinValue}
            fieldName="vin"
            control={control as unknown as Control}
            autoFocus={true}
            inputProps={{
              maxLength: 17,
              onKeyDown: handleKeyDown,
            }}
            InputProps={{
              endAdornment: (
                <Button
                  variant="text"
                  onClick={() => setIsVINModalOpen(true)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                  disableRipple
                >
                  <HelpOutline sx={{ fontSize: 20 }} />
                </Button>
              ),
            }}
            customOnChange={handleCustomChange}
          />
        </VehicleInputsErrorTextPositionWrapper>
      );
    };

    const errorClassName = useMemo(() => {
      return Object.keys(errors).length > 0 ? 'error' : '';
    }, [errors]);

    return (
      <>
        <Box sx={{ width: '100%' }}>
          {isMobile ? (
            <VehicleSearchFieldContainer className={errorClassName}>
              <SearchInputComponent />
            </VehicleSearchFieldContainer>
          ) : (
            <SearchInputComponent />
          )}
        </Box>
        <WhereIsMyVINModal
          isOpen={isVINModalOpen}
          handleClose={() => {
            setIsVINModalOpen(false);
          }}
        />
      </>
    );
  }
);
